import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Box,
  Button,
  TextField,
  InputAdornment,
  Card,
  Typography,
  Chip,
  Checkbox,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from "@mui/material";
import useBulkSelection from "../../../hooks/useBulkSelection";
import BulkActionBar from "../../../Components/common/BulkActionBar";
import PageLoader from "../../../Components/Loader/PageLoader";
import { useGetInventoryQuery } from "../../../Api/attoDeskApi";
import { IProductInventory } from "../../../Api/Interface/api.interface";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { useNavigate, Link } from "react-router-dom";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import { CiSearch } from "react-icons/ci";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import TuneIcon from "@mui/icons-material/Tune";
import Pagination from "../../../Components/Pagination";
import StockAdjustmentDialog, { AdjustableProduct } from "../Adjustments/StockAdjustmentDialog";

type StockStatus = "good" | "low" | "out";

const computeStatus = (stock: number): StockStatus => {
  if (stock <= 0) return "out";
  if (stock < 10) return "low";
  return "good";
};

const STATUS_CHIP: Record<StockStatus, { label: string; bg: string; color: string }> = {
  good: { label: "In Stock", bg: "#dcfce7", color: "#166534" },
  low: { label: "Low", bg: "#fef3c7", color: "#92400e" },
  out: { label: "Out", bg: "#fee2e2", color: "#991b1b" },
};

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"" | StockStatus>("");
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(100);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Stock Adjustment dialog state
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustTarget, setAdjustTarget] = useState<AdjustableProduct | null>(null);

  const openAdjust = (row: IProductInventory) => {
    if (!row.productId) return;
    setAdjustTarget({
      id: Number(row.productId),
      productName: row.productName,
      current_stock: Number(row.finalStock ?? row.totalStock ?? 0),
    });
    setAdjustOpen(true);
  };

  const { data, isLoading, isError, refetch } = useGetInventoryQuery({
    searchText: currentSearchQuery,
    id: "",
  });

  useEffect(() => {
    if (debouncedSearchQuery.length >= 4 || debouncedSearchQuery === "") {
      setCurrentSearchQuery(debouncedSearchQuery);
    }
  }, [debouncedSearchQuery]);

  const handleChangePage = (_event: unknown, newPage: number) => setPage(newPage);

  const coursings: IProductInventory[] = Array.isArray(data?.data)
    ? (data?.data as IProductInventory[])
    : [];

  // Local filter pipeline (search + status)
  const filteredRows = useMemo(() => {
    const term = (searchQuery || "").trim().toLowerCase();
    return coursings.filter((row) => {
      if (term && !(row.productName || "").toLowerCase().includes(term)) return false;
      if (statusFilter) {
        const s = computeStatus(Number(row.finalStock ?? row.totalStock ?? 0));
        if (s !== statusFilter) return false;
      }
      return true;
    });
  }, [coursings, searchQuery, statusFilter]);

  // KPIs from full unfiltered set so they reflect the catalog at a glance
  const kpis = useMemo(() => {
    const total = coursings.length;
    let good = 0, low = 0, out = 0;
    coursings.forEach((r) => {
      const s = computeStatus(Number(r.finalStock ?? r.totalStock ?? 0));
      if (s === "good") good++; else if (s === "low") low++; else out++;
    });
    return { total, good, low, out };
  }, [coursings]);

  const bulk = useBulkSelection<IProductInventory & { id: number }>(filteredRows as any);
  const API_URL = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token") || "";
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  };

  const handleBulkDelete = async () => {
    if (!bulk.selectedIds.length) return;
    if (!window.confirm(`Delete ${bulk.selectedIds.length} selected inventory items?`)) return;
    try {
      const res = await fetch(`${API_URL}/inventory/bulk-delete`, {
        method: "POST",
        headers,
        body: JSON.stringify({ ids: bulk.selectedIds }),
      });
      const json = await res.json();
      if (json.status) {
        showMessage(json.message || "Inventory deleted");
        bulk.clearSelection();
        refetch();
      } else {
        showErrorMessage(json.message || "Bulk delete failed");
      }
    } catch {
      showErrorMessage("Bulk delete failed");
    }
  };

  const resetFilters = () => {
    setSearchQuery("");
    setCurrentSearchQuery("");
    setStatusFilter("");
  };

  const handleExport = () => {
    const rows = filteredRows.map((r, i) => ({
      "#": i + 1,
      Product: r.productName,
      Serialized: r.isSerialized ? "Yes" : "No",
      Stock: r.totalStock,
      Adjusted: r.manualStock,
      Available: r.finalStock,
      Cost: r.avgBuyPrice,
      "Selling Price": r.sellingPrice,
      Status: STATUS_CHIP[computeStatus(Number(r.finalStock ?? r.totalStock ?? 0))].label,
    }));
    const headerKeys = Object.keys(rows[0] || { Product: "" });
    const csv = [
      headerKeys.join(","),
      ...rows.map((row) => headerKeys.map((k) => `"${String((row as any)[k] ?? "").replace(/"/g, '""')}"`).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `inventory-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) return <PageLoader height="40vh" />;

  const hasActiveFilters = !!(searchQuery || statusFilter);
  const visibleRows = filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ bgcolor: "#F6F8FA", minHeight: "100vh", p: { xs: 2, md: 3 } }}>
      {/* HEADER */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography fontSize={22} fontWeight={700} color="#0f172a">Stock Levels</Typography>
          <Typography fontSize={13} color="#64748b">Monitor and manage product inventory</Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon sx={{ fontSize: 16 }} />}
            onClick={handleExport}
            sx={{ borderRadius: "8px", textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: "#E5E7EB", color: "#0f172a", "&:hover": { borderColor: "#0f172a", bgcolor: "#F8FAFC" } }}
          >
            Export
          </Button>
          <Button
            variant="contained"
            disableElevation
            startIcon={<TuneIcon sx={{ fontSize: 16 }} />}
            onClick={() => { setAdjustTarget(null); setAdjustOpen(true); }}
            sx={{ borderRadius: "8px", textTransform: "none", fontSize: 12, fontWeight: 600, bgcolor: "#0f172a", "&:hover": { bgcolor: "#1e293b" } }}
          >
            Adjust Stock
          </Button>
        </Box>
      </Box>

      {/* KPI CARDS */}
      <Grid container spacing={2} mb={3}>
        {[
          { label: "All Products", value: kpis.total, accent: "#0f172a" },
          { label: "In Stock", value: kpis.good, accent: "#22c55e" },
          { label: "Low Stock", value: kpis.low, accent: "#f59e0b" },
          { label: "Out of Stock", value: kpis.out, accent: "#ef4444" },
        ].map((c) => (
          <Grid item xs={12} sm={6} md={3} key={c.label}>
            <Card sx={{ borderRadius: "12px", border: "1px solid #E5E7EB", borderLeft: `4px solid ${c.accent}`, p: 2.25, boxShadow: "none" }}>
              <Typography fontSize={11} fontWeight={600} color="#64748b" textTransform="uppercase" letterSpacing={0.5}>{c.label}</Typography>
              <Typography fontSize={26} fontWeight={700} color="#0f172a" letterSpacing="-0.02em">{c.value}</Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* FILTER BAR */}
      <Box display="flex" gap={1.5} mb={2} flexWrap="wrap" alignItems="center">
        <TextField
          size="small"
          placeholder="Search product..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><CiSearch style={{ color: "#94a3b8" }} /></InputAdornment> }}
          sx={{ minWidth: 280, "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: 13, bgcolor: "#fff", "& fieldset": { borderColor: "#E5E7EB" }, "&:hover fieldset": { borderColor: "#cbd5e1" }, "&.Mui-focused fieldset": { borderColor: "#0f172a", borderWidth: "1.5px" } } }}
        />
        <Select
          size="small"
          displayEmpty
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as any)}
          sx={{ minWidth: 160, borderRadius: "10px", fontSize: 13, bgcolor: "#fff", "& fieldset": { borderColor: "#E5E7EB" } }}
        >
          <MenuItem value="">All Status</MenuItem>
          <MenuItem value="good">In Stock</MenuItem>
          <MenuItem value="low">Low Stock</MenuItem>
          <MenuItem value="out">Out of Stock</MenuItem>
        </Select>

        {/* Quick filter chips */}
        {(["good", "low", "out"] as StockStatus[]).map((s) => {
          const c = STATUS_CHIP[s];
          const active = statusFilter === s;
          return (
            <Chip
              key={s}
              label={c.label}
              size="small"
              clickable
              onClick={() => setStatusFilter(active ? "" : s)}
              sx={{
                height: 32, fontWeight: 600, fontSize: 12, borderRadius: "8px",
                bgcolor: active ? c.color : c.bg,
                color: active ? "#fff" : c.color,
                border: `1px solid ${active ? c.color : c.bg}`,
                "&:hover": { bgcolor: active ? c.color : c.bg, opacity: 0.9 },
              }}
            />
          );
        })}

        {hasActiveFilters && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />}
            onClick={resetFilters}
            sx={{ borderRadius: "10px", textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: "#E5E7EB", color: "#0f172a", height: 36, "&:hover": { borderColor: "#0f172a", bgcolor: "#f8fafc" } }}
          >
            Reset
          </Button>
        )}

        {hasActiveFilters && (
          <Typography sx={{ fontSize: 12, color: "#64748b", ml: 0.5 }}>
            Showing {filteredRows.length} of {coursings.length}
          </Typography>
        )}
      </Box>

      {/* BULK ACTION BAR */}
      <BulkActionBar
        selectedCount={bulk.selectedIds.length}
        onClear={bulk.clearSelection}
        onDelete={handleBulkDelete}
      />

      {/* TABLE */}
      <Box sx={{ bgcolor: "#fff", borderRadius: "12px", border: "1px solid #E5E7EB", overflow: "hidden" }}>
        <TableContainer>
          <Table sx={{
            "& .MuiTableHead-root .MuiTableRow-root": { backgroundColor: "#0f172a" },
            "& .MuiTableHead-root .MuiTableCell-root": {
              backgroundColor: "#0f172a",
              backgroundImage: "none",
              color: "#fff",
              fontSize: 10.5,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              borderBottom: "none",
              boxShadow: "none",
              py: 1.5,
            },
          }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }}>
                  <Checkbox
                    size="small"
                    checked={bulk.allSelected}
                    indeterminate={bulk.indeterminate}
                    onChange={() => bulk.toggleAll(filteredRows as any)}
                    sx={{ color: "#fff", "&.Mui-checked": { color: "#fff" }, "&.MuiCheckbox-indeterminate": { color: "#fff" } }}
                  />
                </TableCell>
                <TableCell>#</TableCell>
                <TableCell>Product Name</TableCell>
                <TableCell>Serialized</TableCell>
                <TableCell>Stock</TableCell>
                <TableCell>Adj</TableCell>
                <TableCell>Available</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Cost</TableCell>
                <TableCell>Selling Price</TableCell>
                <TableCell align="center">Adjust</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isError || filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} sx={{ textAlign: "center", py: 8, color: "#94a3b8" }}>
                    <Typography fontSize={14} fontWeight={600} color="#64748b">No products found</Typography>
                    <Typography fontSize={12} color="#94a3b8" mt={0.5}>
                      {hasActiveFilters ? "Try adjusting or resetting your filters" : "Inventory will appear here once products are added"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map((row: IProductInventory, index) => {
                  const status = computeStatus(Number(row.finalStock ?? row.totalStock ?? 0));
                  const sc = STATUS_CHIP[status];
                  return (
                    <TableRow key={row.id} sx={{ "& td": { borderBottom: "1px solid #F1F5F9" }, "&:hover td": { bgcolor: "#F6F8FA" }, transition: "background 0.15s" }}>
                      <TableCell sx={{ width: 40 }}>
                        <Checkbox
                          size="small"
                          checked={!!row.id && bulk.isSelected(row.id as number)}
                          onChange={() => row.id && bulk.toggleOne(row.id as number)}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: "#94a3b8" }}>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{row.productName}</TableCell>
                      <TableCell>
                        {row.isSerialized ? (
                          <Link to={`/product-history/${row.productId}`} style={{ textDecoration: "none" }}>
                            <Chip label="Yes" size="small" clickable
                              sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: "#dcfce7", color: "#166534", borderRadius: "6px" }} />
                          </Link>
                        ) : (
                          <Chip label="No" size="small"
                            sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: "#f1f5f9", color: "#64748b", borderRadius: "6px" }} />
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{row.totalStock}</TableCell>
                      <TableCell sx={{ fontSize: 13, fontWeight: 700, color: Number(row.manualStock) > 0 ? "#16a34a" : Number(row.manualStock) < 0 ? "#dc2626" : "#64748b" }}>
                        {Number(row.manualStock) >= 0 ? `+${row.manualStock}` : row.manualStock}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{row.finalStock}</TableCell>
                      <TableCell>
                        <Chip label={sc.label} size="small"
                          sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: sc.bg, color: sc.color, borderRadius: "999px" }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: "#334155" }}>${row.avgBuyPrice}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: "#334155" }}>${row.sellingPrice}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Adjust Stock">
                          <span>
                            <IconButton size="small" onClick={() => openAdjust(row)}
                              sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: "#f8fafc", color: "#0f172a", border: "1px solid #e2e8f0", "&:hover": { bgcolor: "#e2e8f0" } }}>
                              <TuneIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, py: 1.5, borderTop: "1px solid #F1F5F9" }}>
          <Pagination
            count={filteredRows.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
          />
        </Box>
      </Box>

      <StockAdjustmentDialog
        open={adjustOpen}
        onClose={() => { setAdjustOpen(false); setAdjustTarget(null); }}
        defaultProduct={adjustTarget}
        products={coursings
          .filter((r) => r.productId)
          .map((r) => ({
            id: Number(r.productId),
            productName: r.productName,
            current_stock: Number(r.finalStock ?? r.totalStock ?? 0),
          }))}
        onSuccess={() => refetch()}
      />
    </Box>
  );
};

export default Index;
